import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { ChartElementType } from './LandingCharts';
import { formatToDecimals } from '@lib/helpers/formatting';

interface HalfDonutGraphProps {
	elements: ChartElementType[];
	hoveredElementIndex: number;
	setHoveredElementIndex: (index: number) => void;
}

const HalfDonutGraph: React.FC<HalfDonutGraphProps> = ({ elements, hoveredElementIndex, setHoveredElementIndex }) => {
	const svgWidth = 350;
	const svgHeight = 125;
	const radius = 125;

	const pieChartData = d3
		.pie<ChartElementType>()
		.padAngle(0.01)
		.sort(null)
		.value((element) => element.value)
		.startAngle(-Math.PI / 2)
		.endAngle(Math.PI / 2)(elements);

	const arcGenerator = d3
		.arc<d3.PieArcDatum<ChartElementType>>()
		.innerRadius(radius * 0.75)
		.outerRadius(radius);

	const isFirstRenderRef = useRef(true);

	const pathRefs = useRef<SVGPathElement[]>([]);

	useEffect(() => {
		if (!isFirstRenderRef.current) return;
		isFirstRenderRef.current = false;
		pathRefs.current.forEach((pathElement, arcIndex) => {
			if (!pathElement) return;

			const finalArcData = pieChartData[arcIndex];

			const interpolateStart = d3.interpolate(-Math.PI / 2, finalArcData.startAngle);
			const interpolateEnd = d3.interpolate(-Math.PI / 2, finalArcData.endAngle);

			d3.select(pathElement)
				.transition()
				.duration(1000)
				.attrTween('d', (_datum, _index, _nodes) => {
					return (progress: number) => {
						return arcGenerator({
							startAngle: interpolateStart(progress),
							endAngle: interpolateEnd(progress),
							value: finalArcData.value,
							index: finalArcData.index,
							data: finalArcData.data,
							padAngle: finalArcData.padAngle,
						});
					};
				});
		});
	}, [pieChartData, arcGenerator]);

	const tooltipRef = useRef<HTMLDivElement>(null);
	const onMouseMove = (event: React.MouseEvent<SVGPathElement, MouseEvent>, arcData: d3.PieArcDatum<ChartElementType>) => {
		if (tooltipRef.current) {
			tooltipRef.current.style.opacity = '1';
			tooltipRef.current.style.left = `${event.clientX - 42}px`;
			tooltipRef.current.style.top = `${event.clientY + 27}px`;
			tooltipRef.current.textContent = `${arcData.data.label}: ${formatToDecimals(arcData.data.value)} €`;
		}
	};
	const onMouseLeave = () => {
		if (tooltipRef.current) {
			tooltipRef.current.style.opacity = '0';
		}
	};
	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				height: '100%',
			}}
		>
			<svg width={svgWidth} height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`}>
				<g transform={`translate(${svgWidth / 2}, ${svgHeight})`}>
					{pieChartData.map((arcData, index) => (
						<path
							key={arcData.data.key}
							ref={(pathRef) => {
								pathRefs.current[index] = pathRef;
							}}
							// d={arcGenerator(arcData) || undefined}
							fill={arcData.data.color}
							onMouseEnter={() => {
								setHoveredElementIndex(index);
							}}
							onMouseMove={(event) => onMouseMove(event, arcData)}
							onMouseLeave={onMouseLeave}
						/>
					))}

					<text textAnchor="middle" dy="-24" fontSize="28" fontWeight="600">
						{Number.isFinite(elements[hoveredElementIndex].percentage) ? elements[hoveredElementIndex].percentage?.toFixed(0) : '0'}%
					</text>

					<text textAnchor="middle" dy="0" fontSize="16">
						{elements[hoveredElementIndex].label}
					</text>
				</g>
			</svg>
			<div ref={tooltipRef} className="graph--tooltip" />
		</div>
	);
};

export default HalfDonutGraph;
